import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/system";
import Sparkline from "./Sparkline";

const nameToOntology = {
  "Air Temp": "air_temperature",
  "Barometric Pressure": "barometric_pressure",
  "Water Conductivity": "water_conductivity",
  "Water Depth": "water_depth",
  "Water Temp": "water_temperature",
  "Heat Flux": "heat_flux",
  "Relative Humidity": "relative_humidity",
  "Solar Voltage": "solar_voltage",
  "Vapour Pressure Deficit": "vapour_pressure_deficit",
  "Uncorrected Inner": "uncorrected_inner",
  "Uncorrected Outer": "uncorrected_outer",
  "Battery Voltage": "battery_voltage",
  "VWC D1": "vwc_d1",
  "Temp D1": "temp_d1",
  "VWC D2": "vwc_d2",
  "Temp D2": "temp_d2",
  "VWC D3": "vwc_d3",
  "Temp D3": "temp_d3",
  "VWC D4": "vwc_d4",
  "Temp D4": "temp_d4",
  "In Longwave": "in_longwave",
  "In Shortwave": "in_shortwave",
  "Out Longwave": "out_longwave",
  "Out Shortwave": "out_shortwave",
  "WXT Temp": "wxt.env.temp",
  "WXT Humidity": "wxt.env.humidity",
  "WXT Pressure": "wxt.env.pressure",
  "WXT Wind Speed": "wxt.wind.speed",
  "WXT Wind Direction": "wxt.wind.direction",
  "WXT Rain Accumulation": "wxt.rain.accumulation",
  "WXT Hail Accumulation": "wxt.hail.accumulation",
  "WXT Heater Temp": "wxt.heater.temp",
  "WXT Heater Voltage": "wxt.heater.volt",
  "WXT Heater Status": "wxt.heater.status",
  "AQT Temp": "aqt.env.temp",
  "AQT Humidity": "aqt.env.humidity",
  "AQT Pressure": "aqt.env.pressure",
  "CO": "aqt.gas.co",
  "NO": "aqt.gas.no",
  "NO2": "aqt.gas.no2",
  "Ozone": "aqt.gas.ozone",
  "PM1": "aqt.particle.pm1",
  "PM2.5": "aqt.particle.pm2.5",
  "PM10": "aqt.particle.pm10",
};

const HelpIconLink = styled('a')({
  display: 'inline-flex',
  alignItems: 'center',
  position: 'relative',
  marginLeft: '4px',
  color: '#1c8cc9',
  cursor: 'pointer',
  textDecoration: 'none',
  '&:hover': {
    color: '#155a8a',
  },
});

const HelpIcon = styled(InfoOutlinedIcon)({
  width: '12px',
  height: '12px',
  position: 'relative',
  top: '-2px',
});

function formatRelative(timestamp) {
  // Ensure timestamp is treated as UTC by appending Z if not present
  const timestampUTC = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z';
  const now = Date.now();
  const then = new Date(timestampUTC).getTime();
  const diff = now - then;
  const diffSec = Math.floor(diff / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHours = Math.floor(diffMin / 60);
  
  if (diffSec < 60) return `${diffSec} ${diffSec === 1 ? 'sec' : 'secs'} ago`;
  if (diffMin < 60) return `${diffMin} ${diffMin === 1 ? 'min' : 'mins'} ago`;
  if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
  
  // Use decimal for days (after 24 hours)
  const days = Math.round((diffHours / 24) * 10) / 10;
  return `${days} ${days === 1 ? 'day' : 'days'} ago`;
}

function formatAbsoluteDate(timestamp, timezone = 'UTC') {
  // Ensure timestamp is treated as UTC by appending Z if not present
  const timestampUTC = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z';
  const date = new Date(timestampUTC);
  
  // Only apply timezone conversion for non-UTC timezones
  if (timezone === 'UTC') {
    return date.toLocaleString(undefined, {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'UTC'
    });
  } else {
    return date.toLocaleString(undefined, {
      timeZone: timezone,
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }
}

function KeyValRow({
  locationName,
  displayName,
  value,
  innerValue,
  outerValue,
  timestamp,
  deviceName,
  deviceId,
  deviceType,
  unit,
  timezone,
}) {
  const navigate = useNavigate();
  const ontology =
    (typeof nameToOntology !== "undefined" && nameToOntology[displayName]) ||
    displayName.toLowerCase().replace(/\s+/g, "_");
  const variablesArray = [ontology];
  
  const isSapFlowCombo = displayName === "Uncorrected Inner/Outer";
  if (isSapFlowCombo) {
    variablesArray.push("uncorrected_inner", "uncorrected_outer");
  }
  
  const handleSparklineClick = () => {
    const params = new URLSearchParams({
      location: locationName,
      device_id: deviceId,
      name: isSapFlowCombo ? "uncorrected_inner|uncorrected_outer" : variablesArray[0],
      start: "-24h",
      timezone: timezone || "UTC",
      page: "0"
    });
    navigate(`/query-browser?${params.toString()}`);
  };
  
  const ontologyUrl = `/data/ontology/${variablesArray[0]}`;
  
  let displayValue;
  if (isSapFlowCombo) {
    const innerDisplay = innerValue !== null && innerValue !== undefined 
      ? (unit ? `${innerValue} ${unit}` : innerValue) 
      : "-";
    const outerDisplay = outerValue !== null && outerValue !== undefined 
      ? (unit ? `${outerValue} ${unit}` : outerValue) 
      : "-";
    displayValue = (
      <div>
        <div>{innerDisplay}</div>
        <div>{outerDisplay}</div>
      </div>
    );
  } else {
    displayValue = value !== null && value !== undefined 
      ? (unit ? `${value} ${unit}` : value) 
      : "-";
  }
  
  const isOldData = timestamp ? (Date.now() - new Date(timestamp.endsWith('Z') ? timestamp : timestamp + 'Z').getTime()) > (24 * 60 * 60 * 1000) : false;
  
  return (
    <tr className="border-b align-top hover:bg-gray-50" style={{ cursor: 'pointer' }}>
      <td className="px-3 py-2 align-middle">
        <span className="inline-flex items-center">
          <span className="align-middle">{displayName}</span>
          <Tooltip 
            title="View variable ontology"
            placement="top"
            arrow
          >
            <HelpIconLink
              href={ontologyUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                // Only prevent default for left clicks
                if (e.button === 0 && !e.ctrlKey && !e.metaKey) {
                  e.preventDefault();
                  window.open(ontologyUrl, '_blank', 'noopener,noreferrer');
                }
              }}
              onMouseDown={(e) => {
                // Handle middle click
                if (e.button === 1) {
                  e.preventDefault();
                }
              }}
              aria-label={`View ontology for ${displayName}`}
            >
              <HelpIcon />
            </HelpIconLink>
          </Tooltip>
        </span>
      </td>
      <td className={`px-3 py-2 text-center align-middle whitespace-nowrap ${isOldData ? '' : 'text-gray-500'}`}>
        {timestamp ? (
          <Tooltip 
            title={formatAbsoluteDate(timestamp, timezone)}
            placement="top"
            componentsProps={{ 
              tooltip: { 
                sx: { background: '#000' } 
              } 
            }}
          >
            <span 
              className={isOldData ? 'font-bold' : ''}
              style={{ 
                cursor: 'pointer',
                color: isOldData ? '#b90501' : 'inherit'
              }}
              aria-label={formatAbsoluteDate(timestamp, timezone)}
            >
              {formatRelative(timestamp)}
            </span>
          </Tooltip>
        ) : (
          <span className="">-</span>
        )}
      </td>
      <td className="px-3 py-2">
        <Sparkline
          seedKey={`${locationName}:${deviceName}:${ontology}`}
          variables={isSapFlowCombo ? ["uncorrected_inner", "uncorrected_outer"] : variablesArray}
          locationName={locationName}
          deviceId={deviceId}
          deviceType={deviceType}
          seriesColors={isSapFlowCombo ? [undefined, "#f6872d"] : undefined}
          onClick={handleSparklineClick}
          title="Open in Data"
        />
      </td>
      <td className="px-3 py-2 text-center align-middle font-medium">
        {displayValue}
      </td>
    </tr>
  );
}

function DeviceTable({ locationName, device, sensorData = {}, loading, timezone }) {
  if (loading) {
    return (
      <table className="w-full text-sm key-value border rounded-md overflow-hidden">
        <thead className="bg-purple-900 text-white">
          <tr>
            <th className="px-3 py-2 text-left" colSpan={4}>
              {device.name}
            </th>
          </tr>
        </thead>
        <tbody className="bg-white">
          <tr>
            <td colSpan={4} className="px-3 py-6 text-center">
              <CircularProgress size={20} />
              <span className="ml-2 text-gray-500">Loading...</span>
            </td>
          </tr>
        </tbody>
      </table>
    );
  }
  
  const isMFR = device.type === "MFR Node";
  const isWXT = device.type === "WXT536 Sensor";
  const isAQT = device.type === "AQT530 Sensor";
  
  // MFR nodes without Hydros21 sensor (exclude water sensors)
  const noHydros21 = ["MNLA4O107", "MNLA4O108", "MNLA4O105"];
  const hasHydros21 = !noHydros21.includes(device.serialNumber);
  
  const mfrVariables = [
    ...(hasHydros21 ? ["water_temperature", "water_conductivity", "water_depth"] : []),
    "vwc_d1",
    "temp_d1",
    "vwc_d2",
    "temp_d2",
    "vwc_d3",
    "temp_d3",
    "vwc_d4",
    "temp_d4",
    "relative_humidity",
    "air_temperature",
    "barometric_pressure",
    "vapour_pressure_deficit",
    "in_longwave",
    "in_shortwave",
    "out_longwave",
    "out_shortwave",
    "heat_flux",
    "battery_voltage",
    "solar_voltage",
  ];
  
  const wxtVariables = [
    "wxt.env.temp",
    "wxt.env.humidity",
    "wxt.env.pressure",
    "wxt.wind.speed",
    "wxt.wind.direction",
    "wxt.rain.accumulation",
    "wxt.hail.accumulation",
    "wxt.heater.temp",
    "wxt.heater.volt",
    "wxt.heater.status",
  ];
  
  const aqtVariables = [
    "aqt.env.temp",
    "aqt.env.humidity",
    "aqt.env.pressure",
    "aqt.gas.co",
    "aqt.gas.no",
    "aqt.gas.no2",
    "aqt.gas.ozone",
    "aqt.particle.pm1",
    "aqt.particle.pm2.5",
    "aqt.particle.pm10",
  ];
  
  const sapVariables = ["uncorrected_inner", "uncorrected_outer"];
  const variables = isMFR ? mfrVariables : isWXT ? wxtVariables : isAQT ? aqtVariables : sapVariables;
  
  const displayNames = {
    air_temperature: "Air Temp",
    barometric_pressure: "Barometric Pressure",
    water_conductivity: "Water Conductivity",
    water_depth: "Water Depth",
    water_temperature: "Water Temp",
    heat_flux: "Heat Flux",
    relative_humidity: "Relative Humidity",
    solar_voltage: "Solar Voltage",
    vapour_pressure_deficit: "Vapour Pressure Deficit",
    uncorrected_inner: "Uncorrected Inner",
    uncorrected_outer: "Uncorrected Outer",
    battery_voltage: "Battery Voltage",
    vwc_d1: "VWC D1",
    temp_d1: "Temp D1",
    vwc_d2: "VWC D2",
    temp_d2: "Temp D2",
    vwc_d3: "VWC D3",
    temp_d3: "Temp D3",
    vwc_d4: "VWC D4",
    temp_d4: "Temp D4",
    in_longwave: "In Longwave",
    in_shortwave: "In Shortwave",
    out_longwave: "Out Longwave",
    out_shortwave: "Out Shortwave",
    "wxt.env.temp": "WXT Temp",
    "wxt.env.humidity": "WXT Humidity",
    "wxt.env.pressure": "WXT Pressure",
    "wxt.wind.speed": "WXT Wind Speed",
    "wxt.wind.direction": "WXT Wind Direction",
    "wxt.rain.accumulation": "WXT Rain Accumulation",
    "wxt.hail.accumulation": "WXT Hail Accumulation",
    "wxt.heater.temp": "WXT Heater Temp",
    "wxt.heater.volt": "WXT Heater Voltage",
    "wxt.heater.status": "WXT Heater Status",
    "aqt.env.temp": "AQT Temp",
    "aqt.env.humidity": "AQT Humidity",
    "aqt.env.pressure": "AQT Pressure",
    "aqt.gas.co": "CO",
    "aqt.gas.no": "NO",
    "aqt.gas.no2": "NO2",
    "aqt.gas.ozone": "Ozone",
    "aqt.particle.pm1": "PM1",
    "aqt.particle.pm2.5": "PM2.5",
    "aqt.particle.pm10": "PM10",
  };
  
  return (
    <table className="w-full text-sm key-value border rounded-md overflow-hidden">
      <thead className="bg-purple-900 text-white">
        <tr>
          <th className="px-3 py-2 text-left">{device.name}</th>
          <th className="px-3 py-2 text-center">latest time</th>
          <th className="px-3 py-2 text-left">last day</th>
          <th className="px-3 py-2 text-center">value</th>
        </tr>
      </thead>
      <tbody className="bg-white">
        {!isMFR && !isWXT && !isAQT && (
          <KeyValRow
            locationName={locationName}
            displayName="Uncorrected Inner/Outer"
            innerValue={sensorData.uncorrected_inner?.value}
            outerValue={sensorData.uncorrected_outer?.value}
            timestamp={
              sensorData.uncorrected_inner?.timestamp ?? sensorData.uncorrected_outer?.timestamp
            }
            deviceName={device.name}
            deviceId={device.serialNumber}
            deviceType={device.type}
            unit={sensorData.uncorrected_inner?.unit ?? sensorData.uncorrected_outer?.unit}
            timezone={timezone}
          />
        )}
        {(isMFR || isWXT || isAQT) &&
          variables.map((variable) => {
            const data = sensorData[variable] || {};
            return (
              <KeyValRow
                key={variable}
                locationName={locationName}
                displayName={displayNames[variable] || variable}
                value={data.value}
                timestamp={data.timestamp}
                deviceName={device.name}
                deviceId={device.serialNumber}
                deviceType={device.type}
                unit={data.unit}
                timezone={timezone}
              />
            );
          })}
      </tbody>
    </table>
  );
}

export default function SensorsPanel({ location, sharedSensorData = null, loading = false }) {
  const [timezone, setTimezone] = useState(localStorage.getItem('crocus_timezone') || 'UTC');
  
  useEffect(() => {
    const handleTimezoneChange = (e) => {
      setTimezone(e.detail);
    };
    
    window.addEventListener('timezoneChange', handleTimezoneChange);
    return () => window.removeEventListener('timezoneChange', handleTimezoneChange);
  }, []);
  
  return (
    <div className="mt-4">
      <div className="border rounded-md bg-white">
        <div className="px-4 py-3 border-b bg-gray-50">
          <h2 className="text-lg font-semibold">Sensors</h2>
          <p className="text-xs text-gray-500">
            Mini charts show last 24h; click chart to open Data (all available).
          </p>
        </div>
        <div className="p-4 columns-1 md:columns-2 gap-4">
          {location.devices.map((device) => (
            <div
              key={`${device.serialNumber}-${timezone}`}
              className="break-inside-avoid mb-4 inline-block w-full"
            >
              <DeviceTable
                locationName={location.name}
                device={device}
                sensorData={sharedSensorData ? (sharedSensorData[device.serialNumber] || {}) : {}}
                loading={loading}
                timezone={timezone}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}